import { useTranslation } from 'react-i18next';

function WhatsAppLink({ children, className }: { children: React.ReactNode; className?: string }) {
  const { t } = useTranslation();
  const message = encodeURIComponent(t('whatsapp.message'));
  return (
    <a
      href={`https://wa.me/905313459987?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden bg-dark-950">
      {/* Premium gradient background */}
      <div className="absolute inset-0 bg-dark-950" />
      
      {/* Subtle radial glow */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(212,185,110,0.08) 0%, transparent 70%)'
      }} />
      
      {/* Decorative corner accents */}
      <div className="absolute top-0 left-0 w-40 h-40 md:w-64 md:h-64 border-l border-t border-champagne-500/10 rounded-br-3xl" />
      <div className="absolute bottom-0 right-0 w-40 h-40 md:w-64 md:h-64 border-r border-b border-champagne-500/10 rounded-tl-3xl" />
      
      {/* Subtle dot grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, #d4b96e 1px, transparent 1px)',
        backgroundSize: '48px 48px'
      }} />

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-20 pb-10">
        <div className="animate-slide-up">
          {/* Logo */}
          <div className="mb-6 md:mb-8">
            <img
              src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/99522d9a3a4519144bb99e3bc03ec0b1.jpeg"
              alt="Cemil Kuaför Logo"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover mx-auto ring-2 ring-champagne-500/30"
            />
          </div>
          
          <p className="text-champagne-400 text-xs md:text-sm font-medium tracking-[0.3em] uppercase mb-4 md:mb-6">
            Cemil Kuaför | Hair Design
          </p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-white/60 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-8 md:mb-10 font-light leading-relaxed">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <a
              href="#videos"
              className="group flex items-center gap-2 bg-white text-dark-950 px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-base font-semibold transition-all hover:bg-champagne-100 whitespace-nowrap cursor-pointer"
            >
              <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-dark-950 text-white mr-1">
                <i className="ri-play-fill text-sm md:text-base" />
              </span>
              {t('hero.btnVideos')}
            </a>
            <WhatsAppLink className="flex items-center gap-2 bg-champagne-500 text-dark-950 px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-base font-semibold transition-all hover:bg-champagne-400 whitespace-nowrap cursor-pointer">
              <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-dark-950 text-white mr-1">
                <i className="ri-whatsapp-line text-sm md:text-base" />
              </span>
              {t('hero.btnWhatsApp')}
            </WhatsAppLink>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <a href="#videos" className="flex flex-col items-center gap-1 text-white/30 hover:text-white/60 transition-colors cursor-pointer">
            <span className="text-xs tracking-wider uppercase">{t('videos.title')}</span>
            <i className="ri-arrow-down-line text-lg" />
          </a>
        </div>
      </div>
    </section>
  );
}
